import {
    Autocomplete, Box, Card, CardHeader, Grid, List, ListItem,
    ListItemButton, ListItemText, TextField, ToggleButton, ToggleButtonGroup, Typography
} from '@mui/material'
import { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { Button } from 'src/components/forms'
import { HttpCacheWorkflow } from 'src/http/workflow/httpCacheWorkflow'
import { EntityWithIdAndDescription, EntityWithIdAndDescriptionFields } from 'src/types'
import { SistemaPrincipal } from 'src/types/workflow/cache'
import { GrupoSistemaDTO, EtapaGrupoPermisoDTO } from './types'
import { HttpEtapaGrupoPermiso } from './api'


function TabPermisoPorGrupo() {
    const [principales, setPrincipales] = useState<SistemaPrincipal[]>([])
    const [selectedPrincipal, setSelectedPrincipal] = useState<SistemaPrincipal | null>(null)
    const [sistemas, setSistemas] = useState<EntityWithIdAndDescription[]>([])
    const [selectedSistema, setSelectedSistema] = useState<EntityWithIdAndDescription | null>(null)
    const [grupos, setGrupos] = useState<GrupoSistemaDTO[]>([])
    const [selectedGrupo, setSelectedGrupo] = useState<GrupoSistemaDTO | null>(null)
    const [etapaPermisos, setEtapaPermisos] = useState<EtapaGrupoPermisoDTO[]>([])
    const [editedPermisos, setEditedPermisos] = useState<Record<number, number>>({})

    useEffect(() => {
        HttpCacheWorkflow.getSistemasPrincipales().then(data => setPrincipales(data))
    }, [])

    useEffect(() => {
        setSelectedSistema(null)
        setSistemas([])
        setGrupos([])
        setSelectedGrupo(null)
        setEtapaPermisos([])
        if (!selectedPrincipal) return
        HttpCacheWorkflow.getSistemasPorSistemaPrincipal(selectedPrincipal.id).then(data => setSistemas(data))
    }, [selectedPrincipal])

    useEffect(() => {
        setGrupos([])
        setSelectedGrupo(null)
        setEtapaPermisos([])
        if (!selectedSistema) return
        HttpEtapaGrupoPermiso.getGruposPorSistema(selectedSistema.id).then(data => {
            setGrupos(data)
        })
    }, [selectedSistema])

    useEffect(() => {
        if (!selectedGrupo || !selectedSistema) { setEtapaPermisos([]); return }
        HttpEtapaGrupoPermiso.getEtapaPermisos(selectedSistema.id, selectedGrupo.id).then(data => {
            setEtapaPermisos(data)
            const permisos: Record<number, number> = {}
            data.forEach(e => { permisos[e.idEtapa] = e.permisoTipo })
            setEditedPermisos(permisos)
        })
    }, [selectedGrupo])

    const handlePermisoChange = (idEtapa: number, permiso: number) => {
        setEditedPermisos(prev => ({ ...prev, [idEtapa]: permiso }))
    }

    const handleSave = () => {
        if (!selectedSistema || !selectedGrupo) return Promise.resolve()
        const permisos = etapaPermisos.map(e => ({ idEtapa: e.idEtapa, permisoTipo: editedPermisos[e.idEtapa] ?? 0 }))
        const promise = HttpEtapaGrupoPermiso.updateEtapaPermisos(selectedSistema.id, selectedGrupo.id, permisos)
        toast.promise(promise, { success: 'Permisos actualizados', error: 'Error al guardar', loading: 'Guardando' })
        return promise
    }

    return (
        <Card>
            <CardHeader title='Conf. por Grupo' subheader='Sistema → Grupo → Etapas con permiso' />
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mx: 2, mb: 2, p: 2, borderRadius: 1, bgcolor: 'action.hover' }}>
                <Box sx={{ minWidth: 200, flex: 1 }}>
                    <Autocomplete
                        options={principales}
                        value={selectedPrincipal}
                        onChange={(_, v) => setSelectedPrincipal(v)}
                        getOptionLabel={(o: SistemaPrincipal) => o[EntityWithIdAndDescriptionFields.Description]}
                        isOptionEqualToValue={(o, v) => o.id === v?.id}
                        size='small'
                        renderInput={params => <TextField {...params} label='Sistema Principal *' />}
                    />
                </Box>
                <Box sx={{ minWidth: 220, flex: 2 }}>
                    <Autocomplete
                        disabled={!selectedPrincipal}
                        options={sistemas}
                        value={selectedSistema}
                        onChange={(_, v) => setSelectedSistema(v)}
                        getOptionLabel={(o: EntityWithIdAndDescription) => o[EntityWithIdAndDescriptionFields.Description]}
                        isOptionEqualToValue={(o, v) => o.id === v?.id}
                        size='small'
                        renderInput={params => <TextField {...params} label='Sistema *' />}
                    />
                </Box>
            </Box>

            {selectedSistema ? (
                <Box sx={{ mx: 2, mb: 2 }}>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                            <Card variant='outlined' sx={{ height: 500, display: 'flex', flexDirection: 'column' }}>
                                <CardHeader title='Grupos' titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }} sx={{ pb: 0 }} />
                                <Box sx={{ overflow: 'auto', flex: 1 }}>
                                    <List dense>
                                        {grupos.map(grupo => (
                                            <ListItem key={grupo.id} disablePadding>
                                                <ListItemButton selected={selectedGrupo?.id === grupo.id} onClick={() => setSelectedGrupo(grupo)}>
                                                    <ListItemText primary={grupo.descripcion} />
                                                </ListItemButton>
                                            </ListItem>
                                        ))}
                                        {grupos.length === 0 && (
                                            <ListItem><ListItemText primary='No hay grupos para este sistema' sx={{ color: 'text.secondary', textAlign: 'center' }} /></ListItem>
                                        )}
                                    </List>
                                </Box>
                            </Card>
                        </Grid>
                        <Grid item xs={12} md={8}>
                            <Card variant='outlined' sx={{ height: 500, display: 'flex', flexDirection: 'column' }}>
                                <CardHeader
                                    title={selectedGrupo ? `Etapas con permiso — ${selectedGrupo.descripcion}` : 'Etapas'}
                                    titleTypographyProps={{ variant: 'subtitle1', fontWeight: 600 }}
                                    sx={{ pb: 0 }}
                                    action={selectedGrupo ? (
                                        <Button variant='contained' color='primary' size='small' promise={handleSave}>Guardar</Button>
                                    ) : null}
                                />
                                <Box sx={{ overflow: 'auto', flex: 1 }}>
                                    {selectedGrupo ? (
                                        <List dense disablePadding>
                                            {etapaPermisos.map(etapa => (
                                                <ListItem key={etapa.idEtapa}
                                                    sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', px: 2, py: 0.75 }}>
                                                    <ListItemText primary={etapa.nombre} sx={{ flex: 1, mr: 1 }} />
                                                    <ToggleButtonGroup exclusive size='small'
                                                        value={String(editedPermisos[etapa.idEtapa] ?? 0)}
                                                        onChange={(_, v) => v !== null && handlePermisoChange(etapa.idEtapa, Number(v))}
                                                        sx={{ flexShrink: 0 }}>
                                                        <ToggleButton value='0' sx={{ fontSize: '0.7rem', py: 0.5, px: 1 }}>Sin permiso</ToggleButton>
                                                        <ToggleButton value='1' sx={{ fontSize: '0.7rem', py: 0.5, px: 1 }}>Lectura</ToggleButton>
                                                        <ToggleButton value='2' sx={{ fontSize: '0.7rem', py: 0.5, px: 1 }}>Miembro</ToggleButton>
                                                        <ToggleButton value='3' sx={{ fontSize: '0.7rem', py: 0.5, px: 1 }}>Responsable</ToggleButton>
                                                    </ToggleButtonGroup>
                                                </ListItem>
                                            ))}
                                            {etapaPermisos.length === 0 && (
                                                <ListItem><ListItemText primary='Este grupo no tiene permisos asignados en ninguna etapa' sx={{ color: 'text.secondary', textAlign: 'center' }} /></ListItem>
                                            )}
                                        </List>
                                    ) : (
                                        <Box sx={{ p: 4, textAlign: 'center' }}><Typography color='text.secondary'>Seleccione un grupo para ver sus etapas</Typography></Box>
                                    )}
                                </Box>
                            </Card>
                        </Grid>
                    </Grid>
                </Box>
            ) : (
                <Box sx={{ p: 4, textAlign: 'center' }}><Typography color='text.secondary'>Seleccione un sistema para comenzar</Typography></Box>
            )}
        </Card>
    )
}

export default TabPermisoPorGrupo
